/**
 * The two forms on the Boxes & Banks tab: a box itself, and money moved
 * from one box to another.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";

import DialogShell, { FieldGrid } from "./DialogShell";
import { Field, Pick, Section, asOptions } from "./Fields";
import { api, problemOf, today, money, CURRENCIES } from "./api";

const KINDS = [
  { id: "cash", name: "Cash box · صندوق" },
  { id: "bank", name: "Bank · مصرف" },
];

const BLANK_BOX = {
  name: "",
  kind: "cash",
  currency: "USD",
  opening_balance: "",
  is_active: true,
  notes: "",
};

export function BoxDialog({ open, box, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK_BOX);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      box ? { ...BLANK_BOX, ...box, opening_balance: String(box.opening_balance ?? "") } : BLANK_BOX
    );
  }, [open, box]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!String(form.name).trim()) return setError("Give the box a name");
    if (!form.kind || !form.currency) return setError("Pick the kind and the currency");
    setSaving(true);
    setError("");
    const body = { ...form, opening_balance: Number(form.opening_balance) || 0 };
    try {
      if (box) await api.put(`/boxes/${box.id}`, body);
      else await api.post("/boxes", body);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the box"));
    }
    return null;
  };

  return (
    <DialogShell
      open={open}
      title={box ? `Edit ${box.name}` : "New Cash Box / Bank · صندوق / مصرف"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
    >
      <Section title="The box · الصندوق">
        <Field
          label="Name · الاسم"
          placeholder="e.g. Main cash box, Arab Bank USD"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        <FieldGrid>
          <Pick
            label="Kind · النوع"
            options={KINDS}
            value={form.kind}
            onChange={(id) => set({ kind: id })}
            getLabel={(k) => k.name}
          />
          <Pick
            label="Currency · العملة"
            options={asOptions(CURRENCIES)}
            value={form.currency}
            onChange={(id) => set({ currency: id })}
            getLabel={(c) => c.name}
            helperText={box ? "Locked once the box has movements" : " "}
          />
        </FieldGrid>
      </Section>

      <Section title="Balance · الرصيد">
        <Field
          label="Opening balance · الرصيد الافتتاحي"
          type="number"
          placeholder="0.00"
          value={form.opening_balance}
          onChange={(e) => set({ opening_balance: e.target.value })}
          helperText="What was in it before anything was recorded here"
          InputProps={{
            endAdornment: <InputAdornment position="end">{form.currency}</InputAdornment>,
          }}
        />
        <Field
          label="Notes · ملاحظات"
          placeholder="Account no., IBAN ..."
          value={form.notes || ""}
          onChange={(e) => set({ notes: e.target.value })}
        />
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={
            <Switch
              checked={Boolean(form.is_active)}
              onChange={(e) => set({ is_active: e.target.checked })}
            />
          }
          label="Active - offered on new vouchers"
        />
      </Section>
    </DialogShell>
  );
}

BoxDialog.defaultProps = { box: null };
BoxDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  box: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    opening_balance: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const BLANK_TRANSFER = {
  date: today(),
  from_source_id: "",
  to_source_id: "",
  amount: "",
  to_amount: "",
  reference: "",
  notes: "",
};

export function TransferDialog({ open, boxes, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK_TRANSFER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm({ ...BLANK_TRANSFER, date: today() });
    }
  }, [open]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const from = boxes.find((b) => String(b.id) === String(form.from_source_id));
  const to = boxes.find((b) => String(b.id) === String(form.to_source_id));
  const exchange = from && to && from.currency !== to.currency;

  const save = async () => {
    if (!from || !to) return setError("Pick both boxes");
    if (from.id === to.id) return setError("Pick two different boxes");
    if (!(Number(form.amount) > 0)) return setError("Enter the amount");
    setSaving(true);
    setError("");
    try {
      await api.post("/transfers", {
        ...form,
        amount: Number(form.amount),
        to_amount: exchange ? Number(form.to_amount) || null : null,
      });
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not record the transfer"));
    }
    return null;
  };

  const boxPick = (label, key, picked, other) => (
    <Pick
      label={label}
      options={boxes}
      value={form[key]}
      onChange={(id) => set({ [key]: id })}
      getLabel={(b) => `${b.name} (${b.currency})`}
      getHint={(b) => (b.is_active ? `${money(b.balance)} ${b.currency}` : "switched off")}
      isDisabled={(b) => !b.is_active || String(b.id) === String(other)}
      noOptionsText="No boxes yet"
      helperText={picked ? `Balance ${money(picked.balance)} ${picked.currency}` : " "}
    />
  );

  return (
    <DialogShell
      open={open}
      title="Transfer Between Boxes · تحويل بين الصناديق"
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      saveLabel="Transfer"
      maxWidth="md"
    >
      <Section title="From → To · من → إلى">
        <FieldGrid>
          {boxPick("From · من", "from_source_id", from, form.to_source_id)}
          {boxPick("To · إلى", "to_source_id", to, form.from_source_id)}
        </FieldGrid>
      </Section>

      <Section title="The money · المبلغ">
        <FieldGrid columns={exchange ? 3 : 2}>
          <Field
            label="Amount sent · المبلغ المحوّل"
            type="number"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => set({ amount: e.target.value })}
            InputProps={{
              endAdornment: <InputAdornment position="end">{from?.currency || ""}</InputAdornment>,
            }}
          />
          {exchange && (
            <Field
              label="Amount received · المبلغ الواصل"
              type="number"
              placeholder="0.00"
              value={form.to_amount}
              onChange={(e) => set({ to_amount: e.target.value })}
              helperText={
                Number(form.amount) > 0 && Number(form.to_amount) > 0
                  ? `Rate 1 ${from.currency} = ${(
                      Number(form.to_amount) / Number(form.amount)
                    ).toFixed(4)} ${to.currency}`
                  : "Different currencies - what actually arrived"
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">{to.currency}</InputAdornment>,
              }}
            />
          )}
          <Field
            label="Date · التاريخ"
            type="date"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
        </FieldGrid>
      </Section>

      <Section title="Details · التفاصيل">
        <FieldGrid>
          <Field
            label="Reference · المرجع"
            placeholder="Optional"
            value={form.reference}
            onChange={(e) => set({ reference: e.target.value })}
          />
          <Field
            label="Notes · ملاحظات"
            placeholder="Optional"
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </FieldGrid>
      </Section>
    </DialogShell>
  );
}

TransferDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  boxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
